<template>
  <div class="relative w-full flex flex-col">
    <!-- One label serves both modes: it associates with the native input via
         `for` on desktop, and names the read-only display box via
         `aria-labelledby` on touch (where there is no `#word-input` to point at). -->
    <label
      :id="`${inputId}-label`"
      :for="mode === 'native' ? 'word-input' : undefined"
      class="sr-only"
    >
      {{ STRINGS.aria.labels.enterWord }}
    </label>

    <!-- Desktop / fine-pointer: real focused text field driven by the physical keyboard. -->
    <IconField v-if="mode === 'native'" class="relative flex w-full">
      <InputText
        id="word-input"
        ref="inputElement"
        v-model="internalValue"
        class="word-input"
        :class="{
          'input-disabled': disabled,
          'input-submitted': submitted,
          'has-clear': showClearButton,
        }"
        :disabled="disabled"
        :placeholder="STRINGS.ui.input.wordPlaceholder"
        type="text"
        :maxlength="availableLetters.length || 10"
        autocomplete="off"
        spellcheck="false"
        autocapitalize="off"
        autocorrect="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        inputmode="text"
        enterkeyhint="done"
        :aria-describedby="`${inputId}-help ${inputId}-status`"
        aria-autocomplete="none"
        :aria-invalid="false"
        @input="handleInput"
        @keydown="handleKeyDown"
        @focus="handleFocus"
        @blur="handleBlur"
        data-testid="word-input"
      />

      <InputIcon
        v-if="showClearButton"
        class="!absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center z-[2]"
      >
        <i
          class="pi pi-times text-arena-text-muted cursor-pointer p-1 rounded-sm transition-all duration-300 ease-out text-base flex items-center justify-center min-w-12 min-h-12 hover:text-arena-text-bright hover:bg-white/5 focus-visible:outline-[3px] focus-visible:outline-[rgba(201,168,76,0.6)] focus-visible:outline-offset-2"
          tabindex="0"
          role="button"
          :aria-label="STRINGS.aria.labels.clearWord"
          @click="handleClearClick"
          @keydown="handleIconKeydown"
          data-testid="clear-button"
        />
      </InputIcon>
    </IconField>

    <!-- Touch / coarse-pointer: read-only word display. No <input>, so the OS
         keyboard never appears; the word is driven by the in-app keyboard. -->
    <div
      v-else
      class="rd-input"
      :class="{ 'rd-input--ok': submitted, 'rd-input--disabled': disabled }"
      role="textbox"
      aria-readonly="true"
      :aria-labelledby="`${inputId}-label`"
      data-testid="word-display"
    >
      <span v-if="value" class="rd-input-word">{{ value }}</span>
      <span v-else class="rd-input-ph">{{ STRINGS.ui.input.wordPlaceholder }}</span>
      <span v-if="!submitted && !disabled" class="rd-caret" aria-hidden="true" />
    </div>

    <div :id="`${inputId}-help`" class="sr-only">
      {{ STRINGS.ui.input.wordHelp }} {{ availableLettersText }}.
      {{ STRINGS.ui.input.wordHelpSuffix }}
    </div>

    <div
      :id="`${inputId}-status`"
      class="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ statusMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import { STRINGS } from '@/constants/strings';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import type { Letter } from '@/types/generated';

interface Props {
  disabled: boolean; // Timer expired or input should be disabled
  value: string; // Current input value
  submitted: boolean; // Player has submitted their word
  availableLetters?: Letter[]; // Letters available for word formation
  autoFocus?: boolean; // Should auto-focus on mount/enable
  timerActive?: boolean; // Timer is actively running (for context menu prevention)
  mode?: 'native' | 'display'; // 'native' = real input (desktop); 'display' = read-only (touch)
}

interface Emits {
  (e: 'update:value', value: string): void;
  (e: 'submit', word: string): void;
  (e: 'focus'): void;
  (e: 'blur'): void;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  value: '',
  submitted: false,
  availableLetters: () => [],
  autoFocus: false,
  timerActive: false,
  mode: 'native',
});

const emit = defineEmits<Emits>();

// Template refs
const inputElement = ref<InstanceType<typeof import('primevue/inputtext').default>>();

// Component state
const internalValue = ref(props.value);
const isFocused = ref(false);
const inputId = ref(`word-input-${Math.random().toString(36).slice(2, 11)}`);

/**
 * Computed properties
 */
const availableLettersText = computed(() => {
  if (props.availableLetters.length === 0) {
    return STRINGS.errors.component.noLetters;
  }
  return props.availableLetters.map(letter => letter.char).join(', ');
});

const statusMessage = computed(() => {
  if (props.submitted) {
    return STRINGS.aria.labels.wordSubmittedSuccess;
  }
  if (props.disabled) {
    return STRINGS.errors.component.inputDisabled;
  }
  // In display mode there is no real input to focus, so announce the word as
  // the in-app keyboard builds it.
  if (props.mode === 'display') {
    return props.value;
  }
  if (isFocused.value) {
    return STRINGS.aria.labels.wordInput;
  }
  return '';
});

const showClearButton = computed(() => {
  return internalValue.value.length > 0 && !props.disabled && !props.submitted;
});

/**
 * Watch for external value changes
 */
watch(
  () => props.value,
  newValue => {
    if (newValue !== internalValue.value) {
      internalValue.value = newValue;
    }
  }
);

/**
 * Watch for disabled state changes to handle auto-focus
 */
watch(
  () => props.disabled,
  newDisabled => {
    if (!newDisabled && props.autoFocus && props.mode === 'native') {
      // Timer started or input re-enabled - auto-focus
      focusInput();
    }
  }
);

/**
 * Event handlers
 */
const handleInput = () => {
  // Convert to uppercase for consistency (game letters are uppercase)
  const upperValue = internalValue.value.toUpperCase();
  internalValue.value = upperValue;
  emit('update:value', upperValue);
};

const handleKeyDown = (event: KeyboardEvent) => {
  // Handle Enter key for submission
  if (event.key === 'Enter' && !props.disabled && internalValue.value.trim()) {
    event.preventDefault();
    emit('submit', internalValue.value.trim());
    return;
  }

  // Handle Escape key to clear input
  if (event.key === 'Escape') {
    event.preventDefault();
    internalValue.value = '';
    emit('update:value', '');
    return;
  }

  // Allow only letters (A-Z) and control keys
  const isLetter = /^[A-Za-z]$/.test(event.key);
  const isControlKey = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'Tab',
  ].includes(event.key);
  const isSystemKey = event.ctrlKey || event.metaKey || event.altKey;

  if (!isLetter && !isControlKey && !isSystemKey) {
    event.preventDefault();
  }
};

const handleFocus = () => {
  isFocused.value = true;
  emit('focus');
};

const handleBlur = () => {
  isFocused.value = false;
  emit('blur');
};

const handleClearClick = () => {
  clearInput();
  focusInput();
};

const handleIconKeydown = (event: KeyboardEvent) => {
  // Handle Enter and Space keys for accessibility
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handleClearClick();
  }
};

/**
 * Public methods
 */
const resolveElement = (): HTMLElement | null => {
  const ref = inputElement.value;
  if (!ref) return null;
  const el = (ref as { $el?: HTMLElement }).$el ?? ref;
  if (el instanceof HTMLElement) return el;
  console.warn('WordInput: could not resolve DOM element from ref');
  return null;
};

const focusInput = () => {
  nextTick(() => {
    resolveElement()?.focus({ preventScroll: true });
  });
};

const clearInput = () => {
  internalValue.value = '';
  emit('update:value', '');
};

// Context menu prevention handler
const handleContextMenu = (event: Event) => {
  if (props.timerActive) {
    event.preventDefault();
  }
};

// Focus-reclaim: on desktop the field should stay focused so typing "just
// works", but it must yield politely to any other control the user clicks.
// We only reclaim when focus would otherwise fall to nothing (body / null).
const handleReclaimBlur = () => {
  if (props.disabled || props.submitted || props.mode !== 'native') return;
  setTimeout(() => {
    const active = document.activeElement;
    if (!active || active === document.body) {
      resolveElement()?.focus({ preventScroll: true });
    }
  }, 0);
};

// Auto-focus on mount if enabled and setup browser assistance blocking
onMounted(() => {
  if (props.mode !== 'native') return;

  if (props.autoFocus && !props.disabled) {
    focusInput();
  }

  // Additional browser assistance blocking attributes
  if (inputElement.value) {
    const input = resolveElement();
    if (input instanceof HTMLInputElement) {
      // Additional password manager blocking
      input.setAttribute('data-form-type', 'other');
      input.setAttribute('data-1p-ignore', 'true');
      input.setAttribute('data-lpignore', 'true');
      input.setAttribute('data-bwignore', 'true');

      // Prevent context menu during timer
      input.addEventListener('contextmenu', handleContextMenu);

      // Keep the desktop field focused so typing just works.
      if (props.autoFocus) {
        input.addEventListener('blur', handleReclaimBlur);
      }
    }
  }
});

onBeforeUnmount(() => {
  const input = resolveElement();
  if (input instanceof HTMLInputElement) {
    input.removeEventListener('contextmenu', handleContextMenu);
    input.removeEventListener('blur', handleReclaimBlur);
  }
});

// Expose public methods
defineExpose({
  focusInput,
  clearInput,
  inputElement,
});
</script>

<style scoped>
:deep(.p-iconfield) {
  position: relative;
  display: inline-block;
  width: 100%;
}

:deep(.p-inputicon) {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 0.75rem;
}

.word-input {
  width: 100%;
  min-height: 44px;
  font-size: 1.125rem;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: 2px solid var(--color-arena-border);
  border-radius: var(--radius-input);
  background: var(--color-arena-surface);
  color: var(--color-arena-text-bright);
  caret-color: var(--color-energy-primary);
  padding: 0.75rem 1rem;
  transition:
    background-color 300ms cubic-bezier(0, 0, 0.2, 1),
    border-color 300ms cubic-bezier(0, 0, 0.2, 1),
    color 300ms cubic-bezier(0, 0, 0.2, 1),
    box-shadow 300ms cubic-bezier(0, 0, 0.2, 1);
  font-family: var(--font-mono);
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.word-input:focus,
.word-input:focus-visible {
  outline: none;
  border-color: var(--color-energy-primary);
  box-shadow: 0 0 0 1px rgba(201, 168, 76, 0.35);
}

.word-input::placeholder {
  color: var(--color-arena-text-muted);
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  opacity: 1;
}

.word-input.input-disabled,
.word-input:disabled {
  background: var(--color-arena-void);
  color: var(--color-arena-text-dim);
  border-color: var(--color-arena-border);
  cursor: not-allowed;
  opacity: 0.6;
}

.word-input.input-disabled::placeholder,
.word-input:disabled::placeholder {
  color: var(--color-arena-text-dim);
}

.word-input.input-submitted {
  background: rgba(0, 196, 143, 0.1);
  border-color: var(--color-energy-success);
  color: var(--color-energy-success);
  box-shadow: 0 0 1px var(--color-energy-success);
}

.word-input.has-clear {
  padding-right: 3rem;
}

/* ── Touch display field (no native input) ──────────────────────────────── */
.rd-input {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  border: 2px solid var(--color-arena-border);
  border-radius: 10px;
  background: var(--color-arena-surface);
  padding: 0 14px;
  transition:
    border-color 0.3s var(--ease-smooth),
    background 0.3s;
}

.rd-input-word {
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 22px;
  letter-spacing: 0.06em;
  color: var(--color-arena-text-bright);
}

.rd-input-ph {
  font-family: var(--font-sans);
  font-size: 17px;
  color: var(--color-arena-text-dim);
}

.rd-input--ok {
  background: rgba(0, 196, 143, 0.1);
  border-color: var(--color-energy-success);
}

.rd-input--ok .rd-input-word {
  color: var(--color-energy-success);
}

.rd-input--disabled {
  opacity: 0.6;
}

.rd-caret {
  width: 2px;
  height: 24px;
  background: var(--color-energy-primary);
  margin-left: 3px;
  animation: rd-blink 1s steps(2, start) infinite;
}

@keyframes rd-blink {
  50% {
    opacity: 0;
  }
}

@media (max-width: 479px) {
  .word-input {
    min-height: 48px;
    font-size: 1rem;
    padding: 0.75rem;
  }
}

@media (min-width: 480px) and (max-width: 767px) {
  .word-input {
    min-height: 48px;
    font-size: 1.125rem;
    padding: 0.75rem 1rem;
  }
}

@media (min-width: 768px) {
  .word-input {
    min-height: 48px;
    font-size: 1.25rem;
    padding: 1rem 1.25rem;
  }
}

@media (min-width: 1024px) {
  .word-input {
    min-height: 52px;
    font-size: 1.25rem;
  }
}

@media (prefers-contrast: high) {
  .word-input {
    border: 2px solid currentColor;
    background: Window;
    color: WindowText;
  }

  .word-input:focus {
    border-color: Highlight;
    box-shadow: 0 0 0 3px Highlight;
  }

  .word-input.input-disabled {
    border: 2px dashed currentColor;
    opacity: 0.8;
  }
}

@media (prefers-reduced-motion: reduce) {
  .word-input {
    transition-duration: 0.01ms;
  }

  .rd-caret {
    animation: none;
  }
}

@media screen and (max-width: 768px) {
  .word-input {
    font-size: 16px;
  }
}

.word-input::-webkit-outer-spin-button,
.word-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.word-input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.word-input::-ms-clear {
  display: none;
}

.word-input[type='search']::-webkit-search-decoration,
.word-input[type='search']::-webkit-search-cancel-button {
  -webkit-appearance: none;
}
</style>
